import axios from 'axios';

// Déterminer l'URL de l'API en fonction de l'environnement
// Si REACT_APP_API_URL est défini, l'utiliser, sinon utiliser la logique par défaut
const getApiUrl = () => {
  // Si la variable d'environnement est définie (même vide), l'utiliser
  if (process.env.REACT_APP_API_URL !== undefined) {
    return process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
  }
  // Sinon, utiliser la logique par défaut
  return process.env.NODE_ENV === 'production'
    ? '/api' // URL relative en production (même domaine)
    : 'http://localhost:3002/api'; // URL locale pour le développement
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Configuration de l'intercepteur pour ajouter le token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Suppression du log de token
    } else {
      // Suppression du warning de token
    }
    
    // Si les données sont de type FormData, ne pas définir Content-Type
    // pour permettre à axios de définir automatiquement le bon Content-Type avec boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      // Ajouter des en-têtes spécifiques pour les requêtes multipart/form-data
      config.headers['Accept'] = 'application/json';
      // Suppression du log FormData
    }
    
    // Suppression du log de configuration de requête
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les réponses et les erreurs
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Gérer les erreurs d'authentification (401)
    if (error.response && error.response.status === 401) {
      console.error('Erreur d\'authentification (401):', error.response.data);
      
      // Supprimer le token et rediriger vers la page de login
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      
      // Rediriger vers la page de login
      if (window.location.pathname !== '/admin/login' && window.location.pathname !== '/login') {
        console.log('Redirection vers la page de login suite à une erreur 401');
        window.location.href = '/admin/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth
api.login = (credentials) => api.post('/auth/login', credentials);
api.register = (userData) => api.post('/auth/register', userData);
api.checkAuth = () => api.get('/auth/check-auth');
api.logout = () => api.post('/auth/logout');

// Users
api.getUsers = () => api.get('/users');
api.getUserById = (id) => api.get(`/users/${id}`);
api.createUser = (userData) => api.post('/users', userData);
api.updateUser = (id, userData) => api.put(`/users/${id}`, userData);
api.deleteUser = (id) => api.delete(`/users/${id}`);

// Registrations
api.getRegistrations = () => api.get('/registrations');
api.getRegistrationById = (id) => api.get(`/registrations/${id}`);
api.createRegistration = (data) => api.post('/registrations', data);
api.updateRegistrationStatus = (id, status) => api.put(`/registrations/${id}/status`, { status });
api.updateRegistration = (id, data) => api.put(`/registrations/${id}`, data);
api.deleteRegistration = (id) => api.delete(`/registrations/${id}`);
api.exportRegistrations = () => api.get('/registrations/export', { responseType: 'blob' });

// Contacts
api.getContacts = () => api.get('/contacts');
api.getContactById = (id) => api.get(`/contacts/${id}`);
api.createContact = (data) => api.post('/contacts', data);
api.markContactAsRead = (id) => api.put(`/contacts/${id}/read`);
api.deleteContact = (id) => api.delete(`/contacts/${id}`);

// News
api.getNews = () => api.get('/news');
api.createNews = (newsData) => {
  
  // Ne pas spécifier Content-Type, l'intercepteur s'en chargera
  return api.post('/news', newsData);
};
api.updateNews = (id, newsData) => {
  
  // Ne pas spécifier Content-Type, l'intercepteur s'en chargera
  return api.put(`/news/${id}`, newsData);
};
api.deleteNews = (id) => api.delete(`/news/${id}`);

// Events
api.getEvents = () => api.get('/events');
api.createEvent = (eventData) => {
  
  return api.post('/events', eventData, {
    headers: {
      'Content-Type': eventData instanceof FormData ? 'multipart/form-data' : 'application/json'
    }
  });
};
api.updateEvent = (id, eventData) => {
  
  return api.put(`/events/${id}`, eventData, {
    headers: {
      'Content-Type': eventData instanceof FormData ? 'multipart/form-data' : 'application/json'
    }
  });
};
api.deleteEvent = (id) => api.delete(`/events/${id}`);

// Stats
api.getStats = () => api.get('/stats');
api.getStatById = (id) => api.get(`/stats/${id}`);
api.createStat = (statData) => api.post('/stats', statData);
api.updateStat = (id, statData) => api.put(`/stats/${id}`, statData);
api.deleteStat = (id) => api.delete(`/stats/${id}`);

export default api;
